import { useQuery } from '@tanstack/react-query';
import {
  ActionTile,
  Avatar,
  BalanceCard,
  EmptyState,
  Flag,
  Icon,
  ListGroup,
  ListRow,
  SectionHeader,
  SegmentedControl,
  SignalMeter,
  Skeleton,
} from '@uplink/ui';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { CURRENCY_SIGNS, type Currency, usePrefs } from '../prefs.tsx';
import { runwayProps } from '../runway.ts';
import { serverState } from '../servers-ui.tsx';
import { haptic } from '../telegram.ts';

const PREVIEW = 3;

export function HomeScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const prefs = usePrefs();
  const { t, money, usd, currency, ratesReady, date } = prefs;
  const me = useQuery(trpc.me.queryOptions());
  const balance = useQuery(trpc.balance.summary.queryOptions());
  const domains = useQuery(trpc.domains.list.queryOptions());
  const servers = useQuery(trpc.servers.list.queryOptions());
  const config = useQuery(trpc.config.queryOptions());

  const hasServers = !!servers.data?.length;
  const hasDomains = !!domains.data?.length;
  const loadingServices = domains.isPending || servers.isPending;

  return (
    <main className="ul-screen">
      {me.data ? (
        <header className="ul-hello">
          <Avatar src={me.data.photoUrl} name={me.data.firstName} size={40} />
          <span className="ul-hello__text">{t.home.greeting(me.data.firstName)}</span>
        </header>
      ) : null}

      {me.data?.isAdmin ? (
        <ListGroup>
          <ListRow icon="chart" title="Админ-панель" chevron onClick={() => nav.push({ name: 'admin' })} />
        </ListGroup>
      ) : null}

      {balance.isPending ? (
        <Skeleton height={232} />
      ) : balance.isError ? (
        <div className="ul-error" role="alert">
          {errorText(balance.error)}
        </div>
      ) : (
        <BalanceCard
          amount={money(balance.data.balanceCents)}
          {...runwayProps(balance.data, { money, date, t })}
          controls={
            <SegmentedControl<Currency>
              label={t.profile.currency}
              value={currency}
              onChange={(nextCurrency) => {
                haptic.select();
                prefs.setCurrency(nextCurrency);
              }}
              options={(Object.keys(CURRENCY_SIGNS) as Currency[]).map((code) => ({
                value: code,
                label: `${CURRENCY_SIGNS[code]} ${code}`,
              }))}
            />
          }
          approx={
            currency !== 'USD' && ratesReady ? t.balance.inUsd(usd(balance.data.balanceCents)) : undefined
          }
          onHistory={() => nav.tab('balance')}
          onTopUp={() => nav.push({ name: 'topup' })}
          note={
            <>
              <Icon name="bolt" size={16} />
              {t.home.payVia(
                (config.data?.paymentMethods ?? ['cryptopay']).map((m) =>
                  m === 'xrocket' ? '@xRocket' : '@CryptoBot',
                ),
              )}
            </>
          }
        />
      )}

      <div className="ul-tiles">
        <ActionTile
          icon="globe"
          title={t.home.domain}
          subtitle={
            config.data?.domainFromCents
              ? t.home.domainFrom(money(config.data.domainFromCents))
              : t.home.domainHint
          }
          onClick={() => nav.tab('domains')}
        />
        <ActionTile
          icon="server"
          title={t.home.server}
          subtitle={
            config.data?.serverFromCents
              ? t.home.serverFrom(money(config.data.serverFromCents))
              : t.home.serverHint
          }
          onClick={() => nav.tab('servers')}
        />
        <ActionTile
          icon="shield"
          title={t.servers.createVpn}
          subtitle={t.desk.vpnSub}
          onClick={() => nav.push({ name: 'serverCreate', vpn: true })}
        />
        {config.data?.sharedVpn.available ? (
          <ActionTile
            icon="key"
            title={t.sharedVpn.shortTitle}
            subtitle={t.sharedVpn.from(money(config.data.sharedVpn.priceMonthly))}
            onClick={() => nav.push({ name: 'vpnSubscription' })}
          />
        ) : null}
      </div>

      {hasServers ? (
        <div className="ul-stack">
          <SectionHeader
            title={t.home.myServers}
            action={servers.data!.length > PREVIEW ? t.common.allN(servers.data!.length) : t.common.all}
            onAction={() => nav.tab('servers')}
          />
          <div className="ul-group">
            {servers.data!.slice(0, PREVIEW).map((s) => {
              const st = serverState(s, t);
              return (
                <ListRow
                  key={s.id}
                  leading={<Flag country={s.country} size={28} />}
                  title={s.name}
                  subtitle={[s.city, s.ipv4 ?? st.label].join(' · ')}
                  trailing={<SignalMeter state={st.state} label={st.label} />}
                  chevron
                  onClick={() => nav.push({ name: 'server', id: s.id })}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      {hasDomains ? (
        <div className="ul-stack">
          <SectionHeader
            title={t.home.myDomains}
            action={domains.data!.length > PREVIEW ? t.common.allN(domains.data!.length) : t.common.all}
            onAction={() => nav.tab('domains')}
          />
          <div className="ul-group">
            {domains.data!.slice(0, PREVIEW).map((d) => (
              <ListRow
                key={d.id}
                icon="globe"
                title={d.name}
                subtitle={
                  d.status === 'active'
                    ? `${t.common.until(d.expiresAt ? date(d.expiresAt) : '—')}${d.autoRenew ? ` · ${t.domains.autoRenewOn}` : ''}`
                    : t.domains.registering
                }
                chevron
                onClick={() => nav.push({ name: 'domain', id: d.id })}
              />
            ))}
          </div>
        </div>
      ) : null}

      {!hasServers && !hasDomains && !loadingServices ? (
        <EmptyState icon="server" title={t.home.emptyTitle} text={t.home.emptyText} />
      ) : null}
    </main>
  );
}
